import { Box, InputLabel, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useMemo, useState } from 'react';

import { disabledFormInputStyles } from '@Components/Form/styles';
import { FormInput } from '@Components/Form';
import { INFO_CATEGORY_RISK_RELATED } from '@Domains/RiskRelated';
import { useGetInfoCategoryMasterData } from '@Domains/MasterData';
import { validateTextEn } from '@Utils/validation';
import Flex from '@Components/Bases/Flex';
import SearchCheckboxList from '@Components/SearchCheckboxList';

import RiskRelatedCheckboxList from './RiskRelatedCheckboxList';

const mappingRiskLabel = (group: INFO_CATEGORY_RISK_RELATED) => {
  switch (group) {
    case INFO_CATEGORY_RISK_RELATED.STRATEGIC_RISK:
      return 'ความเสี่ยงด้านกลยุทธ์และการประกอบธุรกิจ (Strategic Risk)';
    case INFO_CATEGORY_RISK_RELATED.OPERATIONAL_RISK:
      return 'ความเสี่ยงด้านการบริหารจัดการและการปฏิบัติงาน (Operational Risk)';
    case INFO_CATEGORY_RISK_RELATED.COMPLIANCE_RISK:
      return 'ความเสี่ยงด้านกฎระเบียบและกฎหมาย (Compliance Risk)';
    case INFO_CATEGORY_RISK_RELATED.FINANCIAL_RISK:
      return 'ความเสี่ยงด้านการเงิน (Financial Risk)';
    default:
      return;
  }
};

export enum OTHER_RISKS_ID {
  STRATEGIC_RISK = 45,
  OPERATIONAL_RISK = 70,
  COMPLIANCE_RISK = 78,
  FINANCIAL_RISK = 86,
}

const ModalRiskRelatedCheckboxList = () => {
  const { watch } = useFormContext();
  const [filterText, setFilterText] = useState<string>('');

  const [{ data, loading }] = useGetInfoCategoryMasterData([
    INFO_CATEGORY_RISK_RELATED.STRATEGIC_RISK,
    INFO_CATEGORY_RISK_RELATED.OPERATIONAL_RISK,
    INFO_CATEGORY_RISK_RELATED.COMPLIANCE_RISK,
    INFO_CATEGORY_RISK_RELATED.FINANCIAL_RISK,
  ]);

  const filteredRiskList = useMemo(
    () =>
      data?.filter((risk) =>
        risk.nameTh.toLowerCase().includes(filterText.toLowerCase())
      ) ?? [],
    [data, filterText]
  );

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  return (
    <Flex flexDirection="column" width={1} maxWidth={600}>
      <Typography mb={2}>กรุณาเลือกหัวข้อความเสี่ยงที่เกี่ยวข้อง</Typography>
      <SearchCheckboxList
        filterText={filterText}
        handleChange={handleFilterChange}
      />
      <RiskRelatedCheckboxList filteredRisks={filteredRiskList} />
      {filteredRiskList.map((risk, index) => {
        const isOtherRisk = Object.values(OTHER_RISKS_ID).includes(risk.id);
        if (!isOtherRisk) return null;

        const checkedOther = watch(`riskId${risk.id}.checked`);
        const label = mappingRiskLabel(
          risk.infoCategory as INFO_CATEGORY_RISK_RELATED
        );

        return (
          <Box mt={1} key={index}>
            <InputLabel sx={{ my: 1, fontWeight: 800 }} required={checkedOther}>
              {label}
            </InputLabel>
            <Flex flexDirection={'column'} gap={1} width={1}>
              <FormInput
                name={`riskId${risk.id}.nameTh`}
                placeholder={`กรุณาระบุ${label} (ไทย)`}
                rules={{ required: 'กรุณาระบุข้อมูล' }}
                sx={!checkedOther ? disabledFormInputStyles : undefined}
              />
              <FormInput
                name={`riskId${risk.id}.nameEn`}
                placeholder={`กรุณาระบุ${label} (อังกฤษ)`}
                rules={{
                  required: 'กรุณาระบุข้อมูล',
                  pattern: {
                    value: validateTextEn,
                    message: 'กรุณาระบุข้อมูลเป็นภาษาอังกฤษ',
                  },
                }}
                sx={!checkedOther ? disabledFormInputStyles : undefined}
              />
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
};

export default ModalRiskRelatedCheckboxList;
